import { memo, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import { useLiveQueryImmediate } from "@/db/hooks"
import { scheduleTask, updateTaskDueDate, bulkUpdateDueDate } from "@/db/operations"
import { focusTask, unfocusTask, isTaskFocused } from "@/db/operations"
import { deleteReminder } from "@/db/operations"
import { parseDbDate } from "@totodo/operations"
import { StartAtType, StartAtSubType } from "@/db/schema"
import type { DbStartAtIndex, DbObject, DbReminder } from "@/db/schema"
import { toDbDate, parseDbDayjs } from "@/shared/utils/date/dateUtils"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import { InfiniteCalendar } from "./InfiniteCalendar"
import { ReminderSection } from "./ReminderSection"

// ---------------------------------------------------------------------------
// Icons — SVGR imports (pikaicons + totodoicons)
// ---------------------------------------------------------------------------
import StarIcon from "@/shared/icons/totodoicons/solid/star.svg?react"
import MoonIcon from "@/shared/icons/totodoicons/solid/moon.svg?react"
import ArchiveIcon from "@/shared/icons/totodoicons/solid/archive.svg?react"
import CheckIcon from "@/shared/icons/pikaicons/stroke/general/check-tick-single.svg?react"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import FocusTargetIcon from "@/shared/icons/pikaicons/stroke/general/focus-target.svg?react"
import BellAddIcon from "@/shared/icons/pikaicons/solid/alerts/notification-bell-add.svg?react"
import BellIcon from "@/shared/icons/pikaicons/stroke/alerts/notification-bell-on.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * `"schedule"` — full WhenSheet: quick options (Today/Evening/Tomorrow/Someday),
 *   InfiniteCalendar, Reminder, inline Deadline section. Writes to startAtIndex.
 *
 * `"deadline"` — calendar-only: InfiniteCalendar (past dates allowed) + Clear.
 *   Writes directly to task.dueAt.
 */
export type DatePickerMode = "schedule" | "deadline"

interface DatePickerSheetBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  task: DbObject
}

interface DatePickerSheetScheduleProps extends DatePickerSheetBaseProps {
  mode: "schedule"
  startAtData?: DbStartAtIndex
  existingReminder?: DbReminder
  /** All active reminders for the task (sorted by fireAt) */
  allReminders?: DbReminder[]
  /** Map of "YYYY-MM-DD" → count for multi-select highlight */
  highlightedDates?: Map<string, number>
}

interface DatePickerSheetDeadlineProps extends DatePickerSheetBaseProps {
  mode: "deadline"
  /** All selected task IDs for bulk deadline updates */
  taskIds?: string[]
  startAtData?: never
  existingReminder?: never
}

export type DatePickerSheetProps =
  | DatePickerSheetScheduleProps
  | DatePickerSheetDeadlineProps

// ---------------------------------------------------------------------------
// Icon color palette — matches v2 icon registry
// ---------------------------------------------------------------------------
const ICON_COLOR = {
  today: "#F6B51E",
  evening: "#6895FF",
  someday: "#8C71F6",
} as const

// ---------------------------------------------------------------------------
// Quick option type (schedule mode only)
// ---------------------------------------------------------------------------

interface QuickOption {
  key: string
  label: string
  icon: React.ReactNode
  isActive: boolean
  onClick: () => void
}

// ---------------------------------------------------------------------------
// Main DatePickerSheet Component
// ---------------------------------------------------------------------------

/**
 * Unified date picker sheet — the single calendar component for the entire app.
 *
 * Two modes:
 * - **schedule** (startAt): Quick options (Today / This Evening) +
 *   InfiniteCalendar + Someday + Reminder + In Focus + Clear.
 *   Writes to `startAtIndex` via `scheduleTask()`.
 *
 * - **deadline** (dueAt): InfiniteCalendar only (past dates allowed) + Clear.
 *   Writes directly to `task.dueAt` via `updateTaskDueDate()`.
 *
 * Both modes use the same `ResponsiveSheet` + `InfiniteCalendar`, ensuring
 * consistent navigation and visual style across the app. This mirrors v1's
 * `GlobalCustomDatepicker` pattern (two `CustomDatepicker` instances with
 * `type="start"` and `type="due"`), but unified in a single component.
 */
export const DatePickerSheet = memo(function DatePickerSheet(
  props: DatePickerSheetProps,
) {
  const { open, onOpenChange, task, mode } = props
  const taskId = task.id

  if (mode === "deadline") {
    return (
      <DeadlineMode
        open={open}
        onOpenChange={onOpenChange}
        task={task}
        taskId={taskId}
        taskIds={props.taskIds}
      />
    )
  }

  return (
    <ScheduleMode
      open={open}
      onOpenChange={onOpenChange}
      task={task}
      taskId={taskId}
      startAtData={props.startAtData}
      existingReminder={props.existingReminder}
      allReminders={props.allReminders}
      highlightedDates={props.highlightedDates}
    />
  )
})

// ---------------------------------------------------------------------------
// Deadline Mode — calendar-only + clear
// ---------------------------------------------------------------------------

const DeadlineMode = memo(function DeadlineMode({
  open,
  onOpenChange,
  task,
  taskId,
  taskIds,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  task: DbObject
  taskId?: string
  taskIds?: string[]
}) {
  const { t } = useTranslation()
  const currentDue = useMemo(() => {
    const dueMs = parseDbDate(task.dueAt)
    return dueMs === undefined ? undefined : dayjs(dueMs)
  }, [task.dueAt])

  const handleDateSelect = useCallback(
    async (date: dayjs.Dayjs) => {
      const ids = taskIds?.length ? taskIds : taskId ? [taskId] : []
      if (ids.length === 0) return
      if (ids.length === 1) {
        await updateTaskDueDate(ids[0]!, toDbDate(date))
      } else {
        await bulkUpdateDueDate(ids, toDbDate(date))
      }
      onOpenChange(false)
    },
    [taskId, taskIds, onOpenChange],
  )

  const handleClear = useCallback(async () => {
    const ids = taskIds?.length ? taskIds : taskId ? [taskId] : []
    if (ids.length === 0) return
    if (ids.length === 1) {
      await updateTaskDueDate(ids[0]!, undefined)
    } else {
      await bulkUpdateDueDate(ids, undefined)
    }
    onOpenChange(false)
  }, [taskId, taskIds, onOpenChange])

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("Deadline")}
    >
      <div className="flex flex-col gap-0.5 overflow-y-auto px-3 pb-4 pt-2">
        {/* Header */}
        <div className="flex items-center px-2 pb-1 pt-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {t("Deadline")}
          </span>
        </div>

        {/* InfiniteCalendar — same as schedule mode but no minDate */}
        <div className="px-1 pb-1 pt-1">
          <InfiniteCalendar
            selectedDate={currentDue}
            onSelect={handleDateSelect}
            minDate={undefined}
            height={300}
          />
        </div>

        {/* Clear deadline */}
        {task.dueAt && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm",
              "text-destructive transition-colors hover:bg-destructive/10",
            )}
          >
            <CrossIcon className="size-4" />
            <span className="font-medium">{t("Clear deadline")}</span>
          </button>
        )}

        {/* Safe area spacer for iOS/Android */}
        <div className="safe-area-bottom" />
      </div>
    </ResponsiveSheet>
  )
})

// ---------------------------------------------------------------------------
// Schedule Mode — full WhenSheet: quick options + calendar + reminder + deadline
// ---------------------------------------------------------------------------

const ScheduleMode = memo(function ScheduleMode({
  open,
  onOpenChange,
  task: _task,
  taskId,
  startAtData,
  existingReminder: _existingReminder,
  allReminders = [],
  highlightedDates,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  task: DbObject
  taskId?: string
  startAtData?: DbStartAtIndex
  existingReminder?: DbReminder
  allReminders?: DbReminder[]
  highlightedDates?: Map<string, number>
}) {
  const { t } = useTranslation()
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)
  const [reminderSheetOpen, setReminderSheetOpen] = useState(false)
  const [editingReminder, setEditingReminder] = useState<DbReminder | undefined>(undefined)
  const [isFocused] = useLiveQueryImmediate(
    () => (taskId ? isTaskFocused(taskId) : Promise.resolve(false)),
    ["datepicker-focused", taskId],
    false,
  )
  const selectedStartMs = parseDbDate(startAtData?.startAt)
  // Detect current schedule state for checkmarks
  const currentState = useMemo(() => {
    if (!startAtData?.startAt) return "none"
    const isSomeday = startAtData.type === StartAtType.Someday
    const startMs = parseDbDate(startAtData.startAt)
    if (startMs === undefined) return isSomeday ? "someday" : "none"
    const date = dayjs(startMs)
    const today = dayjs()
    const isEvening = startAtData.subType === StartAtSubType.ThisEvening
    const isTomorrow = date.isSame(today.add(1, "day"), "day")

    if (isSomeday) return "someday"
    if (isEvening) return "evening"
    if (date.isSame(today, "day") || date.isBefore(today, "day")) return "today"
    if (isTomorrow && startAtData?.subType !== StartAtSubType.ThisEvening)
      return "tomorrow"
    return "custom"
  }, [startAtData])

  // ── Schedule handlers ─────────────────────────────────────────

  const handleSchedule = useCallback(
    async (
      date: dayjs.Dayjs | undefined,
      type?: StartAtType,
      subType?: StartAtSubType,
    ) => {
      if (!taskId) return
      await scheduleTask(
        taskId,
        date ? toDbDate(date) : undefined,
        type,
        subType,
      )
    },
    [taskId],
  )

  const handleToday = useCallback(
    () => handleSchedule(dayjs(), StartAtType.Default, StartAtSubType.Default),
    [handleSchedule],
  )

  const handleEvening = useCallback(() => {
    const date = dayjs().hour() >= eveningHour ? dayjs().add(1, "day") : dayjs()
    return handleSchedule(date, StartAtType.Default, StartAtSubType.ThisEvening)
  }, [handleSchedule, eveningHour])

  const handleSomeday = useCallback(
    () => handleSchedule(dayjs(), StartAtType.Someday, StartAtSubType.Default),
    [handleSchedule],
  )

  const handleCalendarSelect = useCallback(
    (date: dayjs.Dayjs) => {
      handleSchedule(date, StartAtType.Default, StartAtSubType.Default)
    },
    [handleSchedule],
  )

  const handleClear = useCallback(() => {
    handleSchedule(undefined)
  }, [handleSchedule])

  const handleFocus = useCallback(async () => {
    if (!taskId) return
    if (isFocused) {
      await unfocusTask(taskId)
    } else {
      await focusTask(taskId)
    }
  }, [taskId, isFocused])

  // ── Quick options ─────────────────────────────────────────────

  const options: QuickOption[] = useMemo(
    () => [
      {
        key: "today",
        label: t("Today"),
        icon: (
          <StarIcon className="size-[22px]" style={{ color: ICON_COLOR.today }} />
        ),
        isActive: currentState === "today",
        onClick: handleToday,
      },
      {
        key: "evening",
        label: t("This Evening"),
        icon: (
          <MoonIcon className="size-[22px]" style={{ color: ICON_COLOR.evening }} />
        ),
        isActive: currentState === "evening",
        onClick: handleEvening,
      },
    ],
    [currentState, handleToday, handleEvening],
  )

  return (<>
    <ResponsiveSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t("Schedule task")}
    >
      <div className="flex flex-col overflow-y-auto pb-4">
        <div className="flex flex-col px-3 pt-2">
          {/* Quick options — Today, This Evening */}
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={opt.onClick}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px]",
                "transition-colors hover:bg-muted/50",
                opt.isActive && "bg-muted",
              )}
            >
              <span className="shrink-0">{opt.icon}</span>
              <span className="flex-1 text-left font-medium">{opt.label}</span>
              {opt.isActive && <CheckIcon className="size-5 text-muted-foreground" />}
            </button>
          ))}

          {/* InfiniteCalendar — always visible */}
          <div className="px-1 pb-1 pt-1">
            <InfiniteCalendar
              selectedDate={
                selectedStartMs !== undefined
                  ? dayjs(selectedStartMs)
                  : undefined
              }
              onSelect={handleCalendarSelect}
              minDate={dayjs()}
              height={264}
              highlightedDates={highlightedDates}
            />
          </div>

          {/* Below-calendar options — Someday, Reminder, In Focus */}
          <div className="mt-1 flex flex-col border-t border-border/30 pt-1">
            {/* Someday */}
            <button
              type="button"
              onClick={handleSomeday}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px]",
                "transition-colors hover:bg-muted/50",
                currentState === "someday" && "bg-muted",
              )}
            >
              <ArchiveIcon className="size-[22px]" style={{ color: ICON_COLOR.someday }} />
              <span className="flex-1 text-left font-medium">{t("Someday")}</span>
              {currentState === "someday" && <CheckIcon className="size-5 text-muted-foreground" />}
            </button>

            {/* Reminders — list existing + add new */}
            {allReminders.length > 0 && (
              <div className="flex flex-col">
                {allReminders.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setEditingReminder(r)
                      setReminderSheetOpen(true)
                    }}
                    className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted/50"
                  >
                    <BellIcon className="size-[22px] text-muted-foreground" />
                    <span className="flex-1 text-left font-medium">
                      {parseDbDayjs(r.fireAt)?.format("ddd D MMM HH:mm") ?? ""}
                    </span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteReminder(r.id)
                      }}
                      className="flex size-6 items-center justify-center rounded-full opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
                    >
                      <CrossIcon className="size-3.5 text-muted-foreground" />
                    </span>
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setEditingReminder(undefined)
                setReminderSheetOpen(true)
              }}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted/50"
            >
              <BellAddIcon className="size-[22px] text-muted-foreground" />
              <span className="flex-1 text-left font-medium">{t("Add Reminder")}</span>
            </button>

            {/* In Focus */}
            <button
              type="button"
              onClick={handleFocus}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px]",
                "transition-colors hover:bg-muted/50",
                isFocused && "bg-muted",
              )}
            >
              <FocusTargetIcon className="size-[22px] text-destructive/70" />
              <span className="flex-1 text-left font-medium">{t("In Focus")}</span>
              {isFocused && <CheckIcon className="size-5 text-muted-foreground" />}
            </button>
          </div>

          {/* Clear date — always last */}
          {startAtData?.startAt && (
            <div className="mt-3 border-t border-border/30 pt-3">
              <button
                type="button"
                onClick={handleClear}
                className={cn(
                  "flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold",
                  "bg-destructive/10 text-destructive transition-colors hover:bg-destructive/20",
                )}
              >
                <span>{t("Clear")}</span>
              </button>
            </div>
          )}

          {/* Safe area spacer for iOS/Android */}
          <div className="safe-area-bottom" />
        </div>
      </div>
    </ResponsiveSheet>

    {/* Reminder sheet — separate overlay (must be outside parent sheet) */}
    <ReminderSection
      taskId={taskId ?? ""}
      startAtData={startAtData}
      existingReminder={editingReminder}
      dueAt={_task.dueAt}
      open={reminderSheetOpen}
      onOpenChange={(open) => {
        setReminderSheetOpen(open)
        if (!open) setEditingReminder(undefined)
      }}
    />
  </>
  )
})
